import React, { useEffect, useRef, useState } from 'react';
import { Image, Rect, Transformer } from 'react-konva';
import useImage from 'use-image';

import { getCanvasImageCrossOriginMode } from '../../../utils/canvasImageLoader';

/** Dedicated presentation-image renderer. Keeping this separate from Token is
 * the hard boundary that prevents scene art from entering combat selection,
 * initiative, targeting, or rules resolution. date_of_change: 2026-09-11 */
const SceneAssetNode = ({
  asset,
  editable = false,
  selected = false,
  revealHidden = false,
  onSelect,
  onChange,
  onContextMenu,
}) => {
  const shapeRef = useRef(null);
  const transformerRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [image] = useImage(asset.imageUrl || '', getCanvasImageCrossOriginMode(asset.imageUrl));
  const canTransform = editable && asset.locked !== true;
  const renderedOpacity = asset.visible === false && revealHidden
    ? Math.min(Number(asset.opacity) || 1, 0.35)
    : Number(asset.opacity);

  useEffect(() => {
    if (!transformerRef.current || !shapeRef.current) return;
    transformerRef.current.nodes(selected && canTransform ? [shapeRef.current] : []);
    transformerRef.current.getLayer()?.batchDraw();
  }, [canTransform, selected]);

  if (asset.visible === false && !revealHidden) return null;

  const select = (event) => {
    event.cancelBubble = true;
    if (!editable) return;
    onSelect?.(asset.assetInstanceId);
  };

  const finishTransform = () => {
    const node = shapeRef.current;
    if (!node) return;
    const width = Math.max(4, node.width() * node.scaleX());
    const height = Math.max(4, node.height() * node.scaleY());
    node.scaleX(1);
    node.scaleY(1);
    onChange?.({ x: node.x(), y: node.y(), width, height, rotation: node.rotation() });
  };

  return (
    <>
      {!image && (
        <Rect
          x={asset.x}
          y={asset.y}
          width={asset.width}
          height={asset.height}
          fill="rgba(200, 168, 78, 0.14)"
          stroke="rgba(200, 168, 78, 0.65)"
          dash={[4, 4]}
          listening={false}
        />
      )}
      <Image
        ref={shapeRef}
        id={`scene-asset:${asset.assetInstanceId}`}
        name="scene-asset"
        image={image}
        x={asset.x}
        y={asset.y}
        width={asset.width}
        height={asset.height}
        rotation={asset.rotation}
        opacity={Number.isFinite(renderedOpacity) ? renderedOpacity : 1}
        draggable={canTransform}
        onClick={select}
        onTap={select}
        onMouseEnter={(event) => {
          setHovered(true);
          if (event.target?.getStage()?.container()) event.target.getStage().container().style.cursor = canTransform ? 'move' : 'pointer';
        }}
        onMouseLeave={(event) => {
          setHovered(false);
          if (event.target?.getStage()?.container()) event.target.getStage().container().style.cursor = '';
        }}
        onDragEnd={(event) => onChange?.({ x: event.target.x(), y: event.target.y() })}
        onTransformEnd={finishTransform}
        onContextMenu={(event) => {
          event?.evt?.preventDefault?.();
          event.cancelBubble = true;
          onContextMenu?.(event, asset);
        }}
      />
      {image && editable && (hovered || asset.visible === false || (selected && !canTransform)) && (
        <Rect
          x={asset.x}
          y={asset.y}
          width={asset.width}
          height={asset.height}
          rotation={asset.rotation}
          stroke="#c8a84e"
          strokeWidth={2}
          dash={asset.locked === true || asset.visible === false ? [6, 4] : undefined}
          opacity={selected ? 1 : 0.72}
          listening={false}
        />
      )}
      {selected && canTransform && (
        <Transformer
          ref={transformerRef}
          rotateEnabled
          flipEnabled={false}
          keepRatio={false}
          borderStroke="#c8a84e"
          anchorFill="#151c2e"
          anchorStroke="#c8a84e"
          anchorSize={10}
          boundBoxFunc={(oldBox, nextBox) => (
            nextBox.width < 4 || nextBox.height < 4 ? oldBox : nextBox
          )}
        />
      )}
    </>
  );
};

export default SceneAssetNode;
